import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, MessageCircle, Youtube } from "lucide-react";
import { Container } from "./Primitives";
import { COMPANY, PAYMENT_BADGES, SOCIAL } from "../content/nav";

const SOCIAL_ICON = {
  instagram: Instagram, youtube: Youtube, facebook: Facebook, whatsapp: MessageCircle,
};

const COLUMNS = [
  {
    title: "Shop",
    links: [
      ["Gemstones", "/shop/gemstones"], ["Rudraksha", "/shop/rudraksha"], ["Malas", "/shop/malas"],
      ["Bracelets", "/shop/bracelets"], ["Pendants", "/shop/pendants"], ["Yantras", "/shop/yantras"],
      ["Semi-precious collection", "/collection/semi-precious"],
      ["New arrivals", "/shop/new"], ["Sold archive", "/shop/sold-archive"],
    ],
  },
  {
    title: "Consult",
    links: [
      ["All consultations", "/consult"], ["Our consultants", "/about/consultants"],
      ["Scope of practice", "/consult/scope-of-practice"], ["Puja services", "/consult/puja"],
      ["Annual plan", "/consult/annual-plan"], ["If you are in crisis", "/consult/if-you-are-in-crisis"],
    ],
  },
  {
    title: "Guides & verify",
    links: [
      ["Verify a certificate", "/tools/verify-a-certificate"], ["How we source", "/about/how-we-source"],
      ["Labs we refuse", "/about/labs-we-refuse"], ["What energisation is", "/about/energisation"],
      ["Price-per-carat table", "/guide/gemstones/blue-sapphire-price-per-carat"],
      ["Our scoreboard", "/about/scoreboard"], ["Guides", "/guide"],
      ["Festival calendar", "/tools/muhurat-calendar"],
      ["Rudraksha finder", "/tools/rudraksha-finder"],
      ["Carat–ratti converter", "/tools/carat-ratti-converter"], ["Blog", "/blog"],
    ],
  },
  {
    title: "Support",
    links: [
      ["The Twelve Promises", "/promises"], ["About", "/about"], ["Contact", "/contact"],
      ["Shipping", "/legal/shipping"], ["Returns & refunds", "/legal/refunds"],
      ["Terms", "/legal/terms"], ["Privacy", "/legal/privacy"],
      ["Consent notice", "/legal/consent-notice"], ["Cookies", "/legal/cookies"],
      ["Disclaimer", "/legal/disclaimer"], ["Grievance officer", "/legal/grievance"],
      ["Consumer redressal", "/legal/consumer-redressal"],
    ],
  },
];

function SocialRow() {
  return (
    <div className="mt-7 flex items-center gap-2.5" data-testid="footer-social">
      {SOCIAL.map(({ label, key, url }) => {
        const Icon = SOCIAL_ICON[key];
        const pending = !url || url.startsWith("[[");
        const cls =
          "flex h-9 w-9 items-center justify-center border border-white/15 text-wash/70 transition-colors duration-300 hover:border-gold-bright hover:text-gold-bright";
        return pending ? (
          <span
            key={key}
            className={`${cls} cursor-default opacity-50`}
            title={`${label} handle to be confirmed`}
            aria-label={`${label} — handle to be confirmed`}
            data-testid={`footer-social-${key}`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
        ) : (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cls}
            aria-label={label}
            data-testid={`footer-social-${key}`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </a>
        );
      })}
    </div>
  );
}

export function Footer() {
  return (
    <footer className="mt-8 bg-deep text-wash" data-testid="site-footer">
      <Container className="py-16">
        <div className="footer-grid">
          <div>
            <Link to="/" className="flex items-baseline gap-2" data-testid="footer-logo">
              <span className="deva text-xl leading-none text-gold-bright">श्रीं</span>
              <span
                className="text-base leading-none text-wash"
                style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: "3px" }}
              >
                Shreem Mantra Energy
              </span>
            </Link>
            <p className="ui measure mt-6 text-sm leading-[1.7] text-wash/70">
              Certified gemstones and rudraksha, verified on the laboratory's own website — not ours.
              Flat-fee Vedic guidance from consultants who earn nothing from what you buy.
            </p>
            <SocialRow />
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <p className="ui mb-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold-bright">
                {col.title}
              </p>
              <ul className="space-y-1.5">
                {col.links.map(([label, to]) => (
                  <li key={to + label}>
                    <Link to={to} className="ui text-sm text-wash/70 transition-colors hover:text-wash">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>

      <div className="border-t border-white/10">
        <Container className="flex flex-col gap-5 py-6 md:flex-row md:items-center md:justify-between">
          <p className="ui text-xs text-wash/60" data-testid="footer-copyright">
            © {new Date().getFullYear()} Shreem Mantra Energy. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-2" data-testid="footer-payments">
            {PAYMENT_BADGES.map((p) => (
              <span
                key={p}
                className="ui border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-wash/60"
              >
                {p}
              </span>
            ))}
          </div>
        </Container>
      </div>

      <div className="border-t border-white/10">
        <Container className="py-8">
          <div className="ui space-y-1 text-xs leading-relaxed text-wash/60" data-testid="footer-legal-block">
            <p className="text-wash/85">Shreem Mantra Energy</p>
            <p>
              Company details, registered office, GSTIN and Grievance Officer contact will be
              published here at launch. We acknowledge every complaint within 24 hours and resolve
              within 15 days.
            </p>
          </div>
        </Container>
      </div>

      <div className="border-t border-white/10">
        <Container className="py-6">
          <p className="ui measure text-xs leading-relaxed text-wash/55" data-testid="footer-disclaimer">
            Gemstones, rudraksha, malas and yantras are sold as traditional, ornamental and spiritual
            items. Nothing on this site is medical, psychological, legal or financial advice, and no
            outcome is guaranteed.{" "}
            <Link to="/legal/disclaimer" className="text-gold-bright underline decoration-gold-bright/40 hover:decoration-gold-bright">
              Read our full disclaimer →
            </Link>
          </p>
        </Container>
      </div>
    </footer>
  );
}
